import { useEffect, useMemo, useRef } from 'react';
import { wakeCallables } from '@officehub/offline-sdk';
import { knockFetch, useSignedInUid } from './useWakeScreen';
import { useKeepWarmSwitch } from './useKeepWarmSwitch';
import { hiddenKeepWarmNames, keepWarmNames } from './keepWarmSet';
import { KEEP_WARM_THROTTLE_MS, startKeepWarmLoop } from './keepWarmLoop';
import { wakeArm, wakeKnocksAllowed, wakeUrlFor } from '../config/wakeKnock';
import type { ModuleId } from '../contexts/ModuleContext';

/** Pause before the first pass on app open, so it follows the app shell's own list instead of landing with it. */
export const KEEP_WARM_FIRST_DELAY_MS = 3000;

const isVisible = (): boolean => typeof document === 'undefined' || document.visibilityState !== 'hidden';

/**
 * Call once in the app shell (L-0868). While the app is open and the tab visible, keeps warm every accessible module's
 * landing list, the current module's list and nav/sub-nav pages, and the pages one click from `pathname`: a pass on app open
 * (after KEEP_WARM_FIRST_DELAY_MS), on every module or page change, when the tab is shown or the device comes back online, and every 2 minutes, re-knocking each name 9-11 minutes
 * after its last knock (see keepWarmLoop.ts for the measured 15-minute warm window). A HIDDEN tab keeps only the small core set warm (hiddenKeepWarmNames: this page's calls + this module's entry and landing lists), for at most KEEP_WARM_HIDDEN_MAX_MS since a tick first saw it hidden (a loop restart, e.g. a route change in a background tab, starts that clock again); then nothing until it is shown. With the kill switch off a hidden tab knocks nothing.
 *
 * Same gates as useWakeScreen: no uid = nothing; the build kill switch and the emulator check; only the user's own
 * pilot arm. Remote Config `wake_keep_warm_enabled` = false stops the repeat without a deploy (useKeepWarmSwitch): then
 * only one pass per app open and per module change, with no re-pass on tab show or reconnect.
 */
export function useKeepWarm(
  moduleIds: readonly ModuleId[],
  currentModule: ModuleId | null | undefined,
  hasAccess: (moduleId: ModuleId) => boolean,
  pathname?: string | null,
): void {
  const uid = useSignedInUid();
  const repeat = useKeepWarmSwitch();
  // Compared by content (a joined string), never by array identity, so a re-render with equal lists keeps the loop.
  const key = useMemo(
    () => keepWarmNames(moduleIds, currentModule, hasAccess, pathname).join(','),
    [moduleIds, currentModule, hasAccess, pathname],
  );
  const hiddenKey = useMemo(
    () => hiddenKeepWarmNames(currentModule, hasAccess, pathname).join(','),
    [currentModule, hasAccess, pathname],
  );
  // The uid whose first pass has gone out: until then every (re)start waits KEEP_WARM_FIRST_DELAY_MS, so a restart caused
  // by the switch loading or by a fast module change cannot land on top of the app-shell list; a new user waits again.
  const knockedFor = useRef<string | null>(null);

  useEffect(() => {
    if (uid === null || key === '') return undefined;
    const names = key.split(',');
    const hidden = hiddenKey === '' ? [] : hiddenKey.split(',');
    const enabled = () => wakeKnocksAllowed() && wakeArm(uid) === 'wake';
    const firstDelayMs = knockedFor.current === uid ? 0 : KEEP_WARM_FIRST_DELAY_MS;

    const loop = startKeepWarmLoop({
      names: () => names,
      knock: (chunk) => {
        knockedFor.current = uid;
        return wakeCallables(chunk, { urlFor: wakeUrlFor, enabled, fetchImpl: knockFetch, throttleMs: KEEP_WARM_THROTTLE_MS });
      },
      repeat,
      isVisible,
      hiddenNames: () => hidden,
      firstDelayMs,
    });
    // Switch OFF = one pass per app open / module change and nothing else: no timer and no re-pass on show or online.
    if (!repeat) return () => loop.stop();
    const again = () => loop.tick();
    const onVisible = () => { if (isVisible()) loop.tick(); };
    window.addEventListener('online', again);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('online', again);
      document.removeEventListener('visibilitychange', onVisible);
      loop.stop();
    };
  }, [uid, key, hiddenKey, repeat]);
}
